// @ts-strict-ignore
import produce from 'immer';

import {renameEvent} from 'common/report/util/events';
import {customEventsStore} from 'common/report/stores/custom-events';
import {Translatable} from 'common/i18n';
import {Section} from 'common/components/items-menu/types';
import {LocalizationStore} from 'common/i18n/store';
import {TagItem} from 'common/components/tag-input/tag-item';

import {SortBy} from '../properties-menu/types';
import {EventsController} from '../contexts/events-controller';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {ActionType, Event, EventItem, EventMenuItem, EventTagMenuItem, SerializedEventItemKeyMap} from './types';

export function filterToBookmarkableEventItem(item: EventItem): Event {
  return produce(item, (itemDraft: EventItem): void => {
    for (const key in itemDraft) {
      if (!SerializedEventItemKeyMap[key]) {
        delete itemDraft[key];
      }
    }
  });
}

export function getEventSelectedLabel(
  selected: Event,
  {
    labelPrefix,
    includeNameOnRename = false,
  }: {
    labelPrefix?: string;
    includeNameOnRename?: boolean;
  } = {},
): Translatable {
  if (!selected) {
    return ``;
  }
  const {renamed, label} = selected;
  let selectedLabel: Translatable = ``;

  // handle custom event loading
  if (selected.id && selected.custom) {
    selectedLabel =
      selected.label || customEventsStore.value[selected.id]?.name || renameEvent(selected.value) || `Loading...`;
  } else {
    selectedLabel = label || renameEvent(selected.value);
  }

  if (renamed) {
    selectedLabel = includeNameOnRename ? `${renamed} (${selectedLabel})` : renamed;
  }

  selectedLabel = labelPrefix ? `${labelPrefix} ${selectedLabel}` : selectedLabel;

  return selectedLabel;
}

export function isEventItem(item: EventMenuItem): item is EventItem {
  return item.action === ActionType.Select || !item.action;
}

export function getEventsSections({
  recentlyUsedEvents,
  eventsController,
  dataGroupIds,
  disableAllEventsReason,
  excludeAutotrackEvents,
  excludeCustomEvents,
  mainSectionLabel,
  searchFilter,
  selectedTagSet,
  selected,
  showAllEvents,
  showAnyEvent,
  showTopEvents,
  showSessionEvent,
  showSpecialDatasetProperty,
  otherOptions,
  searchVerified = false,
}: {
  recentlyUsedEvents: EventItem[];
  eventsController: EventsController;
  dataGroupIds: Set<string | null>;
  disableAllEventsReason?: string;
  excludeAutotrackEvents: boolean;
  excludeCustomEvents: boolean;
  mainSectionLabel?: string;
  searchFilter: string;
  selectedTagSet: Set<string>;
  selected: EventItem;
  showAllEvents: boolean;
  showAnyEvent: boolean;
  showTopEvents: boolean;
  showSessionEvent: boolean;
  showSpecialDatasetProperty: boolean;
  otherOptions: Array<PropertySectionItem<string>>;
  searchVerified?: boolean;
}): Array<Section<EventMenuItem>> {
  const tagFilters: string[] = Array.from(selectedTagSet);
  const hasTagFilter = Boolean(tagFilters.length);
  const recentsSection = eventsController.getRecentsSection(recentlyUsedEvents);
  const eventSections = eventsController.getSections({
    dataGroupIds,
    disableAllEventsReason,
    excludeAutotrackEvents,
    excludeCustomEvents,
    mainSectionShowLimit: 0,
    mainSectionLabel,
    searchFilter,
    selected,
    showAllEvents: showAllEvents && !hasTagFilter && !searchVerified,
    showAnyEvent: showAnyEvent && !hasTagFilter && !searchVerified,
    showTopEvents: showTopEvents && !hasTagFilter && !searchVerified,
    showSessionEvent,
    showSpecialDatasetProperty,
    sortByValue: SortBy.Relevance,
    recentlyUsedEvents,
    tagFilters,
    otherOptions,
    searchVerified,
  });

  return [
    {
      ...recentsSection,
      isRecentSection: true,
      items: recentsSection.items.filter((eventItem) => {
        return Boolean(
          Array.from(selectedTagSet).every((tagFilter) => {
            return Boolean(eventItem.value.tags?.includes(tagFilter));
          }),
        );
      }),
      hideTopDivider: false,
    },
    ...eventSections.map((section) => ({...section, hideTopDivider: false})),
  ].filter((section) => section.items.length > 0) as Array<Section<EventMenuItem>>;
}

export function getTagsSection(eventsController: EventsController): Section<EventTagMenuItem> {
  return eventsController.getEventTagsSection({
    excludeCustomEvents: false,
    showLimit: null,
  });
}

export function getSearchSelectedTagItems({
  selectedTagSet,
  i18n,
  eventsController,
}: {
  selectedTagSet: Set<string>;
  i18n: LocalizationStore;
  eventsController: EventsController;
}): TagItem[] {
  const {print} = i18n;
  return getTagsSection(eventsController)
    .items.filter((tag) => selectedTagSet.has(tag.value.value))
    .map((tag) => {
      return {
        value: tag.value.value,
        label: print(tag.label),
      };
    });
}

export function getSearchTagMenuSections({
  selectedTagSet,
  i18n,
  eventsController,
}: {
  selectedTagSet: Set<string>;
  i18n: LocalizationStore;
  eventsController: EventsController;
}): Array<Section<TagItem>> {
  const tagsSection = getTagsSection(eventsController);
  if (!tagsSection.items.length) {
    return [];
  }
  const {t, print} = i18n;
  return [
    {
      label: t(`query-builder.events-menu.select-tag`),
      alwaysShowLabel: true,
      items: tagsSection.items.map((tag) => {
        return {
          label: print(tag.label),
          value: {
            label: print(tag.label),
            value: tag.value.value,
          },
          isDisabled: selectedTagSet.has(tag.value.value),
        };
      }),
    },
  ];
}

/** appends a manual event entry item */
export function sectionsFromInput({
  searchFilter,
  sections,
  i18n,
}: {
  searchFilter: string;
  sections: Section[];
  i18n: LocalizationStore;
}): Array<Section<EventMenuItem>> {
  const {t} = i18n;
  return [
    ...(sections.every((section) => section.items.length === 0)
      ? [
          {
            label: t(`query-builder.no-search-results`),
            alwaysShowLabel: true,
            items: [],
          },
        ]
      : []),
    {
      items: [
        {
          label: `"${searchFilter}"`,
          value: {
            value: searchFilter,
            label: searchFilter,
          },
          itemFromInput: true,
        },
        {
          label: t(`query-builder.refresh`),
          icon: `refresh`,
          isSpecialAction: true,
          excludeFromInputMatch: true,
          value: {
            action: ActionType.Refresh as const,
          },
        },
      ],
    },
  ];
}
